import { normalizeDesignAnswer, type DesignAreaAnswer, type DesignLayer } from '@pz/scene';

/**
 * "Moji detalji" — Zazzle's Moment: the shopper's names, dates and photos,
 * entered once and offered on every other product of the shop.
 *
 * A template field is matched across products by its `key` — the author's
 * link between duplicate fields — or, where the author set none, by its label
 * ("Ime deteta" on the shirt is "Ime deteta" on the mug). The details live in
 * this browser only (localStorage, per shop origin), are saved only when the
 * shopper asks, and are applied only when the shopper asks: nothing is carried
 * silently into a design. Photos are the shopper's own uploads, which belong to
 * the same browser session on every product page of the shop, so reusing one
 * needs no new upload.
 */

export const MOMENT_STORAGE_KEY = 'pz-moment';
/** A detail older than this is not offered any more. */
export const MOMENT_MAX_AGE_MS = 180 * 86_400_000;
const MAX_FIELDS = 40;
const MAX_TEXT = 400;

export type MomentValue =
  | { kind: 'text'; label: string; text: string }
  | { kind: 'image'; label: string; assetId: string; url: string; w?: number; h?: number };

export interface MomentStore {
  v: 1;
  at: number;
  fields: Record<string, MomentValue>;
}

/** "Ime deteta" → "ime-deteta": the fallback identity of an unkeyed field. */
export function slugLabel(label: string): string {
  return label
    .toLocaleLowerCase('sr-RS')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'dj')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function momentKeyOf(layer: DesignLayer): string | undefined {
  const template = layer.template;
  if (!template) return undefined;
  if (template.key) return template.key;
  const slug = template.label ? slugLabel(template.label) : '';
  return slug ? `label:${slug}` : undefined;
}

export interface MomentField {
  key: string;
  label: string;
  layer: DesignLayer;
}

/** The fillable details a design offers: template text and photo fields, once per key. */
export function momentFieldsOf(answer: DesignAreaAnswer | null | undefined): MomentField[] {
  const fields: MomentField[] = [];
  const seen = new Set<string>();
  for (const layer of answer?.layers ?? []) {
    if (layer.kind !== 'text' && layer.kind !== 'image') continue;
    if (layer.interactive === false) continue;
    const key = momentKeyOf(layer);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    fields.push({ key, label: layer.template?.label ?? key, layer });
  }
  return fields;
}

/** Whether the shopper has made this field their own (not the author's sample). */
function isPersonal(layer: DesignLayer): boolean {
  const original = layer.template?.original;
  if (layer.kind === 'text') return layer.text.trim() !== '' && layer.text !== original;
  if (layer.kind === 'image') return layer.assetId !== original;
  return false;
}

/**
 * The details worth remembering from these designs: every field the shopper
 * changed from the author's sample, merged over what was remembered before.
 */
export function captureMoment(
  answers: Array<DesignAreaAnswer | null | undefined>,
  urlOf: (assetId: string) => string | undefined,
  previous: MomentStore | undefined,
  now = Date.now(),
): MomentStore {
  const fields: Record<string, MomentValue> = { ...(previous?.fields ?? {}) };
  for (const answer of answers) {
    for (const field of momentFieldsOf(answer)) {
      const layer = field.layer;
      if (!isPersonal(layer)) continue;
      if (layer.kind === 'text') {
        fields[field.key] = { kind: 'text', label: field.label, text: layer.text.slice(0, MAX_TEXT) };
      } else if (layer.kind === 'image') {
        const url = urlOf(layer.assetId);
        if (!url || !/^(https?:|blob:|data:)/.test(url)) continue;
        fields[field.key] = {
          kind: 'image',
          label: field.label,
          assetId: layer.assetId,
          url,
          ...(layer.sourcePx ? { w: layer.sourcePx.w, h: layer.sourcePx.h } : {}),
        };
      }
    }
  }
  const kept = Object.entries(fields).slice(-MAX_FIELDS);
  return { v: 1, at: now, fields: Object.fromEntries(kept) };
}

/** The remembered details this design would take, by label, in design order. */
export function momentOffers(answer: DesignAreaAnswer | null | undefined, store: MomentStore | undefined): string[] {
  if (!store) return [];
  const labels: string[] = [];
  for (const field of momentFieldsOf(answer)) {
    const value = store.fields[field.key];
    if (!value) continue;
    const layer = field.layer;
    const differs = value.kind === 'text'
      ? layer.kind === 'text' && layer.text !== value.text
      : layer.kind === 'image' && layer.assetId !== value.assetId;
    if (differs) labels.push(field.label);
  }
  return labels;
}

/**
 * The design with the remembered details written in: every layer of a field
 * (duplicates sharing a key included), text for text and the photo for a
 * photo. A photo's crop belonged to the picture it replaced, so it goes.
 * Returns the assets the preview must learn about.
 */
export function applyMoment(
  answer: DesignAreaAnswer,
  store: MomentStore,
): { answer: DesignAreaAnswer; assets: Array<{ assetId: string; url: string }> } {
  const assets: Array<{ assetId: string; url: string }> = [];
  const layers = answer.layers.map((layer): DesignLayer => {
    if (layer.interactive === false) return layer;
    const key = momentKeyOf(layer);
    const value = key ? store.fields[key] : undefined;
    if (!value) return layer;
    if (value.kind === 'text' && layer.kind === 'text') return { ...layer, text: value.text };
    if (value.kind === 'image' && layer.kind === 'image') {
      if (!assets.some((asset) => asset.assetId === value.assetId)) assets.push({ assetId: value.assetId, url: value.url });
      const next = { ...layer, assetId: value.assetId, ...(value.w && value.h ? { sourcePx: { w: value.w, h: value.h } } : {}) };
      delete next.crop;
      delete next.erase;
      return next;
    }
    return layer;
  });
  return { answer: normalizeDesignAnswer({ ...answer, layers }) ?? answer, assets };
}

export function parseMomentStore(raw: unknown, now = Date.now()): MomentStore | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const store = raw as Partial<MomentStore>;
  if (store.v !== 1 || typeof store.at !== 'number' || now - store.at > MOMENT_MAX_AGE_MS) return undefined;
  if (!store.fields || typeof store.fields !== 'object') return undefined;
  const fields: Record<string, MomentValue> = {};
  for (const [key, value] of Object.entries(store.fields)) {
    if (!value || typeof value !== 'object' || typeof key !== 'string' || key.length > 60) continue;
    const label = typeof value.label === 'string' ? value.label.slice(0, 80) : key;
    if (value.kind === 'text' && typeof value.text === 'string') fields[key] = { kind: 'text', label, text: value.text.slice(0, MAX_TEXT) };
    if (value.kind === 'image' && typeof value.assetId === 'string' && typeof value.url === 'string') {
      fields[key] = { kind: 'image', label, assetId: value.assetId, url: value.url,
        ...(typeof value.w === 'number' && typeof value.h === 'number' ? { w: value.w, h: value.h } : {}) };
    }
  }
  return Object.keys(fields).length ? { v: 1, at: store.at, fields } : undefined;
}

export function readMomentStore(storage: Pick<Storage, 'getItem'> | undefined): MomentStore | undefined {
  try {
    return parseMomentStore(JSON.parse(storage?.getItem(MOMENT_STORAGE_KEY) ?? 'null'));
  } catch {
    return undefined;
  }
}

export function writeMomentStore(storage: Pick<Storage, 'setItem'> | undefined, store: MomentStore): boolean {
  try {
    storage?.setItem(MOMENT_STORAGE_KEY, JSON.stringify(store));
    return Boolean(storage);
  } catch {
    return false;
  }
}
